import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { PageHeader } from '../components/Shell';
import { Waiting } from '../components/Waiting';
import { ApiError } from '../net/client';
import {
  categoryLabel,
  categoryLabels,
  getProductReadiness,
  listAllProducts,
  pillFor,
  type Product,
  type ProductPill,
  type ProductReadiness,
} from '../net/products';
import { useSession } from '../session/useSession';
import { chip, colors, radius, shell, type, verdict } from '../theme';

/**
 * G2, `design/PortalProducts.dc.html`.
 *
 * Every value below is literal in that board. What the board does not draw is
 * D7: every page is loaded because no pager is drawn, search and category
 * filter the loaded list because the API takes neither, "Codes issued" and
 * "First scans" are drawn empty because no route returns them per product,
 * and an empty tenant is the table with no rows.
 */

type Load =
  | { status: 'loading' }
  | { status: 'failed'; error: unknown }
  | { status: 'loaded'; products: Product[]; readiness: Map<string, ProductReadiness> };

/** `grid-template-columns` of the header and every row. */
const columns = '2.2fr 1.2fr 1fr 1fr 0.9fr 40px';

/**
 * The status pills. Active and Needs photo are drawn in the verdict hues:
 * `#E5F4EC`/`#147F47` and `#FCF3D9`/`#A16207` are the board's own values, and
 * they are those tokens byte for byte, so the board is the exception to the
 * token file's "never reuse", not this code.
 */
const pills: Record<ProductPill, { label: string; bg: string; fg: string }> = {
  active: { label: 'Active', bg: verdict.genuine.bg, fg: verdict.genuine.fg },
  needs_photo: { label: 'Needs photo', bg: verdict.suspect.bg, fg: verdict.suspect.fg },
  needs_batch_details: {
    label: 'Needs batch details',
    bg: verdict.suspect.bg,
    fg: verdict.suspect.fg,
  },
  archived: { label: 'Archived', bg: chip.muted.bg, fg: chip.muted.fg },
};

export function Products() {
  const { api } = useSession();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      listAllProducts(api, controller.signal),
      getProductReadiness(api, controller.signal),
    ]).then(
      ([products, readiness]) =>
        setLoad({
          status: 'loaded',
          products,
          readiness: new Map(readiness.map((r) => [r.productId, r])),
        }),
      (error: unknown) => {
        if (!controller.signal.aborted) setLoad({ status: 'failed', error });
      },
    );
    return () => controller.abort();
  }, [api]);

  const visible = useMemo(() => {
    if (load.status !== 'loaded') return [];
    const needle = search.trim().toLowerCase();
    return load.products.filter(
      (p) =>
        (category === '' || p.category === category) &&
        (needle === '' || p.name.toLowerCase().includes(needle)),
    );
  }, [load, search, category]);

  const count = load.status === 'loaded' ? load.products.length : null;
  const subtitle =
    count === null
      ? undefined
      : `${count} ${count === 1 ? 'product' : 'products'} · what consumers see after a genuine scan`;

  return (
    <>
      {load.status === 'loading' ? <Waiting>Loading products</Waiting> : null}
      <PageHeader
        title="Products"
        {...(subtitle !== undefined ? { subtitle } : {})}
        action={
          <button
            className="vp-fill"
            type="button"
            onClick={() => navigate('/products/new')}
            style={newProductButton}
          >
            New product
          </button>
        }
      />
      {load.status === 'failed' ? (
        <Card title="Products could not be loaded" body={failureText(load.error)} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12 }}>
            <input
              type="search"
              aria-label="Search products"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...field, width: 320 }}
            />
            <div style={{ position: 'relative', width: 200 }}>
              <select
                aria-label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  ...field,
                  width: '100%',
                  appearance: 'none',
                  paddingRight: 36,
                  cursor: 'pointer',
                }}
              >
                <option value="">All categories</option>
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                style={{ position: 'absolute', right: 14, top: 15, pointerEvents: 'none' }}
              >
                <path
                  d="M6 9 L12 15 L18 9"
                  stroke={colors.mutedInk}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <ProductTable
            rows={visible}
            readiness={load.status === 'loaded' ? load.readiness : undefined}
            busy={load.status === 'loading'}
          />
          <p style={{ ...type.meta, color: colors.mutedInk, margin: 0 }}>
            Product name, photo and description appear on the consumer's verdict screen: keep them
            exactly as printed on the pack. Products flagged above cannot have codes issued until
            the gap is closed.
          </p>
        </>
      )}
    </>
  );
}

function ProductTable({
  rows,
  readiness,
  busy,
}: {
  rows: Product[];
  readiness: Map<string, ProductReadiness> | undefined;
  busy: boolean;
}) {
  return (
    <div
      role="table"
      aria-label="Products"
      aria-busy={busy}
      style={{
        background: colors.card,
        border: `1px solid ${colors.line}`,
        borderRadius: radius.card,
      }}
    >
      <div
        role="row"
        style={{ ...grid, padding: '14px 22px', borderBottom: `1px solid ${colors.line}` }}
      >
        {['Product', 'Category', 'Codes issued', 'First scans', 'Status'].map((label) => (
          <div
            key={label}
            role="columnheader"
            style={{ ...type.tableHeader, color: colors.mutedInk }}
          >
            {label}
          </div>
        ))}
        <div role="columnheader" aria-label="Open" />
      </div>
      {rows.map((product, i) => {
        const pill = pills[pillFor(product, readiness?.get(product.id))];
        return (
          <div key={product.id} role="rowgroup">
            {i > 0 ? <div style={{ height: 1, background: colors.line }} /> : null}
            <div role="row" style={{ ...grid, alignItems: 'center', padding: '15px 22px' }}>
              <div role="cell" style={{ ...type.bodyStrong, color: colors.navy }}>
                {product.name}
              </div>
              <div role="cell" style={cell}>
                {categoryLabel(product.category)}
              </div>
              {/* No route returns codes issued or first scans per product, DEFERRED 1.5. */}
              <div role="cell" style={cell} />
              <div role="cell" style={cell} />
              <div role="cell">
                <span
                  style={{
                    display: 'inline-flex',
                    padding: '4px 12px',
                    borderRadius: radius.chip,
                    background: pill.bg,
                    color: pill.fg,
                    ...type.captionBold,
                  }}
                >
                  {pill.label}
                </span>
              </div>
              <div role="cell">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 5 L16 12 L9 19"
                    stroke={colors.mutedInk}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function failureText(error: unknown): string {
  if (error instanceof ApiError && error.kind === 'network') {
    return 'The API could not be reached. Reload the page to try again.';
  }
  if (error instanceof Error) return error.message;
  return 'Reload the page to try again.';
}

const grid: CSSProperties = { display: 'grid', gridTemplateColumns: columns, gap: 16 };

const cell: CSSProperties = { ...type.body, color: colors.mutedInk };

const field: CSSProperties = {
  height: 44,
  boxSizing: 'border-box',
  borderRadius: 12,
  background: colors.card,
  border: `1px solid ${colors.line}`,
  padding: '0 14px',
  ...type.body,
  color: colors.navy,
};

const newProductButton: CSSProperties = {
  height: shell.actionButton.height,
  padding: `0 ${shell.actionButton.paddingX}px`,
  borderRadius: shell.actionButton.radius,
  background: colors.brand,
  color: colors.white,
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  ...type.button,
};
